import { apiClient, type PageData } from './client'
import type {
  CreateServiceRecordPayload,
  ServiceRecord,
  ServiceRecordDepartmentStatistics,
  ServiceRecordFilter,
  ServiceRecordStatistics,
  UpdateServiceRecordPayload,
} from '@/types/service-record'

export const serviceRecordsApi = {
  list: async (params?: { page?: number; page_size?: number } & ServiceRecordFilter): Promise<PageData<ServiceRecord>> => {
    const searchParams = new URLSearchParams()
    if (params?.page) searchParams.set('page', String(params.page))
    if (params?.page_size) searchParams.set('page_size', String(params.page_size))
    if (params?.service_type_id) searchParams.set('service_type_id', String(params.service_type_id))
    if (params?.customer_id) searchParams.set('customer_id', String(params.customer_id))
    if (params?.user_id) searchParams.set('user_id', String(params.user_id))
    if (params?.user_ids?.length) searchParams.set('user_ids', params.user_ids.join(','))
    if (params?.start_date) searchParams.set('start_date', params.start_date)
    if (params?.end_date) searchParams.set('end_date', params.end_date)
    if (params?.scope) searchParams.set('scope', params.scope)
    if (params?.department_id) searchParams.set('department_id', String(params.department_id))
    const qs = searchParams.toString()
    const response = await apiClient.get<PageData<ServiceRecord>>(qs ? `/service-records?${qs}` : '/service-records')
    return response.data
  },

  get: async (id: number): Promise<ServiceRecord> => {
    const response = await apiClient.get<ServiceRecord>(`/service-records/${id}`)
    return response.data
  },

  create: async (data: CreateServiceRecordPayload): Promise<ServiceRecord> => {
    const response = await apiClient.post<ServiceRecord>('/service-records', data)
    return response.data
  },

  update: async (id: number, data: UpdateServiceRecordPayload): Promise<ServiceRecord> => {
    const response = await apiClient.put<ServiceRecord>(`/service-records/${id}`, data)
    return response.data
  },

  delete: async (id: number): Promise<void> => {
    await apiClient.delete(`/service-records/${id}`)
  },

  summary: async (
    userId: number,
    params?: { page?: number; page_size?: number; start_date?: string; end_date?: string; scope?: string; department_id?: number },
  ): Promise<PageData<ServiceRecord>> => {
    const searchParams = new URLSearchParams()
    searchParams.set('user_id', String(userId))
    if (params?.page) searchParams.set('page', String(params.page))
    if (params?.page_size) searchParams.set('page_size', String(params.page_size))
    if (params?.start_date) searchParams.set('start_date', params.start_date)
    if (params?.end_date) searchParams.set('end_date', params.end_date)
    if (params?.scope) searchParams.set('scope', params.scope)
    if (params?.department_id) searchParams.set('department_id', String(params.department_id))
    const response = await apiClient.get<PageData<ServiceRecord>>(`/service-records/summary?${searchParams.toString()}`)
    return response.data
  },

  statistics: async (params?: {
    start_date?: string
    end_date?: string
    scope?: string
    department_id?: number
  }): Promise<ServiceRecordStatistics> => {
    const searchParams = new URLSearchParams()
    if (params?.start_date) searchParams.set('start_date', params.start_date)
    if (params?.end_date) searchParams.set('end_date', params.end_date)
    if (params?.scope) searchParams.set('scope', params.scope)
    if (params?.department_id) searchParams.set('department_id', String(params.department_id))
    const qs = searchParams.toString()
    const response = await apiClient.get<ServiceRecordStatistics>(
      qs ? `/service-records/statistics?${qs}` : '/service-records/statistics',
    )
    return response.data
  },

  departmentStatistics: async (params?: {
    start_date?: string
    end_date?: string
    scope?: string
    department_id?: number
  }): Promise<ServiceRecordDepartmentStatistics> => {
    const searchParams = new URLSearchParams()
    if (params?.start_date) searchParams.set('start_date', params.start_date)
    if (params?.end_date) searchParams.set('end_date', params.end_date)
    if (params?.scope) searchParams.set('scope', params.scope)
    if (params?.department_id) searchParams.set('department_id', String(params.department_id))
    const qs = searchParams.toString()
    const response = await apiClient.get<ServiceRecordDepartmentStatistics>(
      qs ? `/service-records/department-statistics?${qs}` : '/service-records/department-statistics',
    )
    return response.data
  },
}
